import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../common/Button';
import { Text } from '../../common/Typography';
import { ClubOverviewHero } from './ClubOverviewHero';
import { ClubPerformanceSnapshot } from './ClubPerformanceSnapshot';
import { ClubSessionsOverviewCarousel } from './ClubSessionsOverviewCarousel';
import { ClubLeaderboardOverviewPreview } from './ClubLeaderboardOverviewPreview';
import { ClubFrequentMatchups } from './ClubFrequentMatchups';
import { ClubActivitySection } from './ClubActivitySection';
import { ClubOverviewSection } from './ClubOverviewSection';
import { ClubLeagueOverviewCarousel } from './ClubLeagueOverviewCarousel';
import {
  fetchClubMoments,
  fetchClubPlayerSessionWinRates,
  fetchClubBalancedRecord,
  fetchClubTopMatchups,
} from '../../../services/clubDashboardService';
import { fetchClubLeagueTeamSnapshots, fetchMyClubLeagueSummary } from '../../../services/leagueService';

/**
 * My Club dashboard — player-centric overview tab (redesigned).
 *
 * Section order:
 *  1. ClubOverviewHero    — banner + meta + rankings + actions
 *  2. ClubPerformanceSnapshot — player KPIs
 *  3. ClubSessionsOverviewCarousel — last played, then upcoming
 *  3b. ClubLeagueOverviewCarousel — personal season if linked, plus club team standings
 *  4. ClubLeaderboardOverviewPreview — top 3 per ladder
 *  5. ClubFrequentMatchups — club-scoped matchup carousel
 *  6. ClubActivitySection — "Your moments"
 */
export function ClubDashboardOverview({
  club,
  clubId,
  clubStats,
  currentUser,
  canManage,
  selectedSeason,
  recentSessions,
  upcomingSessions,
  leaderboard,
  snapshotMovement,
  leaderboardLoading = false,
  sessionsPlayed = null,
  overviewRecap,
  overviewRecapLoading,
  onViewSessionPerformance,
  onViewLeaderboard,
  onViewStats,
  onViewMatchups,
  onViewLeague,
  onOpenManagement,
  onRefresh,
}) {
  const navigate = useNavigate();
  const [leagueSummary, setLeagueSummary] = useState(null);
  const [leagueTeams, setLeagueTeams] = useState([]);
  const leagueEnabled = Boolean(onViewLeague);

  useEffect(() => {
    if (!currentUser?.id || !clubId || !leagueEnabled) {
      setLeagueSummary(null);
      setLeagueTeams([]);
      return undefined;
    }
    let cancelled = false;
    fetchMyClubLeagueSummary(clubId, currentUser.id)
      .then((summary) => { if (!cancelled) setLeagueSummary(summary); })
      .catch((err) => {
        console.error('Failed to load league summary', err);
        if (!cancelled) setLeagueSummary(null);
      });
    fetchClubLeagueTeamSnapshots(clubId)
      .then((teams) => { if (!cancelled) setLeagueTeams(teams); })
      .catch((err) => {
        console.error('Failed to load club league teams', err);
        if (!cancelled) setLeagueTeams([]);
      });
    return () => { cancelled = true; };
  }, [currentUser?.id, clubId, leagueEnabled]);

  const [sessionWinRates, setSessionWinRates] = useState(new Map());
  const [seasonRecord, setSeasonRecord] = useState(null);
  const [seasonRecordLoading, setSeasonRecordLoading] = useState(false);
  const [moments, setMoments] = useState([]);
  const [momentsLoading, setMomentsLoading] = useState(false);
  const [matchups, setMatchups] = useState([]);
  const [matchupsLoading, setMatchupsLoading] = useState(false);

  const recentSessionKey = (recentSessions || []).map((session) => session.id).filter(Boolean).join(',');

  useEffect(() => {
    if (!currentUser?.id || !clubId || !selectedSeason?.id) {
      setSeasonRecord(null);
      setSeasonRecordLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSeasonRecordLoading(true);
    fetchClubBalancedRecord(currentUser.id, clubId, selectedSeason.id)
      .then((record) => { if (!cancelled) setSeasonRecord(record); })
      .catch((err) => {
        console.warn('fetchClubBalancedRecord:', err);
        if (!cancelled) setSeasonRecord(null);
      })
      .finally(() => { if (!cancelled) setSeasonRecordLoading(false); });
    return () => { cancelled = true; };
  }, [currentUser?.id, clubId, selectedSeason?.id]);

  useEffect(() => {
    if (!currentUser?.id || !clubId) return undefined;
    let cancelled = false;
    const sessionIds = recentSessionKey ? recentSessionKey.split(',') : [];
    fetchClubPlayerSessionWinRates(currentUser.id, clubId, sessionIds)
      .then((rates) => { if (!cancelled) setSessionWinRates(rates); })
      .catch(() => { if (!cancelled) setSessionWinRates(new Map()); });
    return () => { cancelled = true; };
  }, [currentUser?.id, clubId, recentSessionKey]);

  useEffect(() => {
    if (!currentUser?.id || !clubId) return;
    let cancelled = false;

    setMomentsLoading(true);
    fetchClubMoments(clubId, 5)
      .then((m) => { if (!cancelled) { setMoments(m); setMomentsLoading(false); } })
      .catch(() => { if (!cancelled) { setMoments([]); setMomentsLoading(false); } });

    setMatchupsLoading(true);
    fetchClubTopMatchups(clubId, 5)
      .then((m) => { if (!cancelled) { setMatchups(m); setMatchupsLoading(false); } })
      .catch(() => { if (!cancelled) { setMatchups([]); setMatchupsLoading(false); } });

    return () => { cancelled = true; };
  }, [currentUser?.id, clubId]);

  const handleViewClubTab = () => navigate(`/clubs/${clubId}?tab=club`);

  return (
    <div className="space-y-5 sm:space-y-6">

      {/* 1. Hero — banner, meta, rankings */}
      <ClubOverviewHero
        club={club}
        clubStats={clubStats}
        selectedSeason={selectedSeason}
        recap={overviewRecap}
        recapLoading={overviewRecapLoading}
        sessionsPlayed={sessionsPlayed}
        onViewLeaderboard={onViewLeaderboard}
      />

      {canManage && onOpenManagement && (
        <div className="rounded-xl border border-border-medium bg-surface-subtle/50 p-3 border-l-4 border-l-primary">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Text className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Admin
            </Text>
            <Button
              variant="primary"
              size="small"
              onClick={onOpenManagement}
              className="w-full sm:w-auto shrink-0 flex items-center justify-center gap-2"
            >
              <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Club management</span>
            </Button>
          </div>
        </div>
      )}

      {/* 2. Performance snapshot — player KPIs */}
      {currentUser && selectedSeason && (
        <ClubPerformanceSnapshot
          recap={overviewRecap}
          recapLoading={overviewRecapLoading}
          seasonRecord={seasonRecord}
          seasonRecordLoading={seasonRecordLoading}
          sessionsPlayed={sessionsPlayed}
          selectedSeason={selectedSeason}
          onViewStats={onViewStats}
        />
      )}

      {/* 3. Sessions — last played, then upcoming */}
      {currentUser && (
        <div id="overview-upcoming-sessions">
          <ClubSessionsOverviewCarousel
            pastFirst
            upcomingSessions={upcomingSessions}
            recentSessions={recentSessions}
            currentUser={currentUser}
            clubId={clubId}
            canManage={canManage}
            sessionWinRates={sessionWinRates}
            onRefresh={onRefresh}
            onViewSessionPerformance={onViewSessionPerformance}
            onViewAll={handleViewClubTab}
            onSectionClick={handleViewClubTab}
          />
        </div>
      )}

      {(leagueSummary || leagueTeams.length > 0) && onViewLeague && (
        <ClubOverviewSection
          variant="league"
          label="League"
          subtitle={leagueSummary ? 'Your matches, then club teams' : 'Club teams in the county league'}
          viewAllLabel="Open League"
          onViewAll={onViewLeague}
          onSectionClick={onViewLeague}
        >
          <ClubLeagueOverviewCarousel summary={leagueSummary} teams={leagueTeams} />
        </ClubOverviewSection>
      )}

      {/* 4. Leaderboard preview — top 3 with ladder tabs */}
      {currentUser && (leaderboardLoading || leaderboard) && (
        <ClubLeaderboardOverviewPreview
          leaderboard={leaderboard}
          snapshotMovement={snapshotMovement}
          leaderboardLoading={leaderboardLoading}
          currentUser={currentUser}
          onViewAll={onViewLeaderboard}
        />
      )}

      {/* 5. Frequent matchups — club-scoped carousel */}
      {currentUser && !matchupsLoading && matchups.length > 0 && onViewMatchups && (
        <ClubOverviewSection
          label="Frequent matchups"
          variant="matchups"
          onViewAll={onViewMatchups}
          onSectionClick={onViewMatchups}
        >
          <ClubFrequentMatchups matchups={matchups} clubId={clubId} embedded />
        </ClubOverviewSection>
      )}

      {/* 6. Your moments */}
      {currentUser && (
        <ClubActivitySection
          moments={moments}
          momentsLoading={momentsLoading}
          currentUser={currentUser}
        />
      )}

    </div>
  );
}
